<script setup>
// UM AVISO AO CLIENTE ESPERANDO APROVAÇÃO (F4.c, 17/10/2026; peça própria em 18/10).
//
// Morava na gaveta das trilhas do Follow-up; agora aparece onde o advogado trabalha — o
// painel do Jurídico e a ficha do processo. Quem aprova precisa ler o que sairia e conferir
// com o original: o tipo do andamento em selo ("confira com cuidado" no que é sensível), o
// texto do tribunal a um clique e o aviso escrito pela IA, editável antes de aprovar.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import avisosAPI from 'dashboard/api/avisosAoCliente';
import { tempoDoAviso } from './avisoAoCliente';

const props = defineProps({
  aviso: { type: Object, required: true },
  // Na ficha do processo, o processo é o da própria página: não precisa do link.
  mostrarProcesso: { type: Boolean, default: true },
});
const emit = defineEmits(['decidido']);

const { t } = useI18n();
const { showAlert } = useAlert();
const A = 'AVISOS_AO_CLIENTE.APROVACAO';

const texto = ref(props.aviso.previa);
const decidindo = ref('');

const foiEditado = computed(
  () => texto.value.trim() !== String(props.aviso.previa || '').trim()
);

const quando = computed(() => {
  const { desde, vence } = tempoDoAviso(
    props.aviso.proposto_em,
    props.aviso.vence_em
  );
  return [
    t(`${A}.${desde.codigo}`, { n: desde.n }),
    t(`${A}.${vence.codigo}`, { n: vence.n }),
  ].join(' · ');
});

const decidir = async acao => {
  decidindo.value = acao;
  try {
    if (acao === 'aprovar') {
      const { data } = await avisosAPI.aprovar(
        props.aviso.id,
        foiEditado.value ? texto.value.trim() : null
      );
      showAlert(t(`${A}.RESULTADO.${data?.estado}`, t(`${A}.RESULTADO.outro`)));
    } else {
      await avisosAPI.descartar(props.aviso.id);
      showAlert(t(`${A}.DESCARTADO`));
    }
  } catch (e) {
    showAlert(e?.response?.data?.error || t(`${A}.ERRO`));
  } finally {
    decidindo.value = '';
    emit('decidido', props.aviso.id);
  }
};
</script>

<template>
  <li class="apa">
    <div class="apa__cab">
      <p class="apa__quem">{{ aviso.cliente }}</p>
      <p class="apa__tempo">{{ quando }}</p>
    </div>
    <p v-if="mostrarProcesso" class="apa__processo">
      <i class="i-ph-folder-simple" aria-hidden="true" />
      <router-link
        :to="{
          name: 'legal_process_show',
          params: { processId: aviso.legal_process_id, aba: 'andamentos' },
        }"
        class="apa__link"
        :title="aviso.processo"
      >
        {{ aviso.pasta || aviso.processo }}
      </router-link>
      <span v-if="aviso.andamentos > 1" class="apa__n">
        {{ t(`${A}.ANDAMENTOS`, { n: aviso.andamentos }) }}
      </span>
    </p>
    <!-- F4.c: o que a IA leu. O tipo em selo; decisão, sentença e recurso pedem cuidado. -->
    <div class="apa__leitura">
      <span
        class="ficha-pill"
        :class="aviso.sensivel ? 'ficha-pill--ambar' : 'ficha-pill--neutro'"
      >
        {{ t(`${A}.TIPO.${aviso.tipo || 'outro'}`, aviso.tipo || '') }}
      </span>
      <span v-if="aviso.sensivel" class="apa__cuidado">
        <i class="i-ph-warning" aria-hidden="true" />
        {{ t(`${A}.CUIDADO`) }}
      </span>
      <span v-if="!aviso.inteligente" class="apa__fixo">
        {{ t(`${A}.TEXTO_FIXO`) }}
      </span>
    </div>
    <details v-if="aviso.originais?.length" class="apa__original">
      <summary>{{ t(`${A}.ORIGINAL`) }}</summary>
      <p v-for="(o, i) in aviso.originais" :key="i">
        <strong>{{ o.data }}</strong> · {{ o.texto }}
      </p>
    </details>
    <textarea
      v-model="texto"
      class="advup-campo reset-base apa__texto"
      rows="6"
      :aria-label="t(`${A}.TEXTO_DO_AVISO`)"
    />
    <div class="apa__acoes">
      <button
        type="button"
        class="advup-btn advup-btn--compacto advup-btn--vazado"
        :disabled="Boolean(decidindo)"
        @click="decidir('descartar')"
      >
        {{ t(`${A}.DESCARTAR`) }}
      </button>
      <button
        type="button"
        class="advup-btn advup-btn-forte advup-btn--compacto"
        :disabled="Boolean(decidindo) || !texto.trim()"
        @click="decidir('aprovar')"
      >
        <i
          :class="
            decidindo === 'aprovar'
              ? 'i-lucide-loader-circle animate-spin'
              : 'i-ph-paper-plane-tilt'
          "
          aria-hidden="true"
        />
        {{ foiEditado ? t(`${A}.APROVAR_EDITADO`) : t(`${A}.APROVAR`) }}
      </button>
    </div>
  </li>
</template>

<style scoped>
.apa {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  min-width: 0;
  padding: var(--esp-4);
  border: 1px solid rgb(var(--slate-4));
  border-radius: var(--r-4);
  background: rgb(var(--solid-1));
}

.apa__cab {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--esp-3);
}

.apa__quem {
  margin: 0;
  min-width: 0;
  overflow: hidden;
  color: rgb(var(--slate-12));
  font-size: var(--txt-corpo);
  font-weight: var(--peso-titulo);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.apa__tempo {
  flex: none;
  margin: 0;
  color: rgb(var(--slate-11));
  font-size: var(--txt-nota);
  font-variant-numeric: tabular-nums;
}

.apa__processo {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  margin: 0;
  min-width: 0;
  color: rgb(var(--slate-11));
  font-size: var(--txt-nota);
}

.apa__processo > i {
  flex: none;
  width: var(--ico-1);
  height: var(--ico-1);
}

.apa__link {
  min-width: 0;
  overflow: hidden;
  color: rgb(var(--blue-11));
  text-overflow: ellipsis;
  white-space: nowrap;
}

.apa__link:hover {
  text-decoration: underline;
}

.apa__n {
  flex: none;
}

.apa__leitura {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--esp-2);
}

.apa__cuidado {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  color: color-mix(in srgb, rgb(var(--amber-11)) 55%, rgb(var(--amber-12)));
  font-size: var(--txt-nota);
}

.apa__cuidado > i {
  width: var(--ico-1);
  height: var(--ico-1);
}

.apa__fixo {
  color: rgb(var(--slate-11));
  font-size: var(--txt-nota);
}

.apa__original {
  color: rgb(var(--slate-11));
  font-size: var(--txt-nota);
  line-height: var(--linha-normal);
}

.apa__original > summary {
  cursor: pointer;
  font-weight: var(--peso-rotulo);
}

.apa__original > p {
  margin: var(--esp-1) 0 0;
  padding: var(--esp-2) var(--esp-3);
  border-radius: var(--r-2);
  background: rgb(var(--slate-2));
  color: rgb(var(--slate-12));
  overflow-wrap: anywhere;
}

/* O aviso que vai sair, editável, na voz da bolha de saída. */
.apa__texto {
  width: 100%;
  min-height: 128px;
  resize: vertical;
  background: rgb(var(--blue-2));
  line-height: var(--linha-normal);
}

.apa__acoes {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--esp-2);
  padding-top: var(--esp-1);
}

/* Celular (medido em 390px na gaveta antiga): o nome cortava ao lado do tempo; o tempo
   desce para baixo do nome. */
@media (max-width: 639px) {
  .apa__cab {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }

  .apa__quem {
    white-space: normal;
  }
}
</style>
